import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

const SECTIONS = [
  { id: "summary", label: "1. Summary" },
  { id: "money-back", label: "2. 14-day money-back guarantee" },
  { id: "how-to-request", label: "3. How to request a refund" },
  { id: "processing", label: "4. Processing time" },
  { id: "renewals", label: "5. Renewals" },
  { id: "exclusions", label: "6. Exclusions" },
  { id: "law", label: "7. Statutory rights" },
  { id: "chargebacks", label: "8. Chargebacks" },
  { id: "cancellation", label: "9. Cancellation" },
  { id: "contact", label: "10. Contact" },
];

export default function RefundPolicy() {
  return (
    <LegalLayout
      title="Refund Policy"
      subtitle="Our refund commitment to you. Short version: 14 days, no questions asked, on new subscriptions."
      lastUpdated="May 18, 2026"
      sections={SECTIONS}
    >
      <section id="summary">
        <h2>1. Summary</h2>
        <ul>
          <li>
            <strong>14-day money-back guarantee</strong> on every new paid
            subscription — monthly or annual.
          </li>
          <li>
            Cancel anytime; you keep access through the end of the current
            paid period.
          </li>
          <li>
            Renewal charges are not refundable except where required by law.
          </li>
        </ul>
      </section>

      <section id="money-back">
        <h2>2. 14-day money-back guarantee</h2>
        <p>
          When you purchase a new <strong>Pro</strong> or <strong>Business</strong>{" "}
          subscription on AgentFlow Compliance you may request a full refund
          within <strong>fourteen (14) calendar days</strong> of the initial
          purchase. This applies to both monthly and annual plans and applies
          to your first purchase of each plan tier only.
        </p>
        <p>
          No reason is required — if AgentFlow doesn't deliver value for you
          in those first two weeks we'll send your money back.
        </p>
      </section>

      <section id="how-to-request">
        <h2>3. How to request a refund</h2>
        <ol>
          <li>
            Email{" "}
            <a href="mailto:hello@agentflow.ventures">hello@agentflow.ventures</a>{" "}
            from the address on your AgentFlow account.
          </li>
          <li>
            Include your <strong>workspace name</strong> and (if available)
            the <strong>order or invoice ID</strong> from the receipt you
            received when you subscribed.
          </li>
          <li>
            We'll confirm receipt within <strong>1 business day</strong> and
            issue the refund through the original payment method.
          </li>
        </ol>
        <p>
          You do not need to fill out a form or justify the request — we
          honour the policy on first ask.
        </p>
      </section>

      <section id="processing">
        <h2>4. Processing time</h2>
        <p>
          Once approved, refunds are processed by our payment provider
          (currently <strong>Paddle.com</strong>, who acts as the merchant of
          record). The refund typically appears on your statement within{" "}
          <strong>5 – 10 business days</strong> depending on your card issuer
          or bank. We'll send you an email confirmation when the refund is
          issued.
        </p>
      </section>

      <section id="renewals">
        <h2>5. Renewals</h2>
        <p>
          Subscriptions renew automatically until cancelled. Renewal charges
          are <strong>not refundable</strong> except where required by law or
          at our discretion in exceptional cases. To avoid an unwanted
          renewal, cancel your subscription before the renewal date — see
          section 9.
        </p>
      </section>

      <section id="exclusions">
        <h2>6. Exclusions</h2>
        <p>The 14-day money-back guarantee does not apply to:</p>
        <ul>
          <li>renewal charges (see section 5);</li>
          <li>repeat purchases of the same plan tier after a previous refund;</li>
          <li>accounts that have been suspended or terminated for breach of our <a href="/terms">Terms of Service</a>;</li>
          <li>accounts that have shown abusive usage patterns (e.g. running thousands of scans within the 14-day window solely to extract value before requesting a refund).</li>
        </ul>
      </section>

      <section id="law">
        <h2>7. Statutory rights</h2>
        <p>
          Some jurisdictions provide statutory cooling-off or refund rights
          beyond what this policy offers — for example, the <strong>EU
          14-day right of withdrawal</strong> for distance contracts.
          Customers in those jurisdictions retain all such rights regardless
          of this policy. Nothing in this policy limits your non-waivable
          consumer rights under applicable law.
        </p>
      </section>

      <section id="chargebacks">
        <h2>8. Chargebacks</h2>
        <p>
          Please contact us before initiating a chargeback with your bank or
          card issuer — we will almost always resolve the issue faster, and a
          chargeback may result in account suspension while the dispute is
          investigated. We honour valid chargebacks initiated for fraud or
          unauthorised charges.
        </p>
      </section>

      <section id="cancellation">
        <h2>9. Cancellation (no refund needed)</h2>
        <p>
          If you simply want to stop being charged at the next renewal, you
          can cancel at any time:
        </p>
        <ol>
          <li>Sign in to AgentFlow.</li>
          <li>Go to <strong>Billing</strong> in the sidebar.</li>
          <li>Click <strong>Cancel</strong> on your current plan.</li>
        </ol>
        <p>
          You will retain full access until the end of the current paid
          period, after which your workspace will downgrade to the Free
          plan automatically. No further charges will be made.
        </p>
      </section>

      <section id="contact">
        <h2>10. Contact</h2>
        <p>
          Refund or billing questions:{" "}
          <a href="mailto:hello@agentflow.ventures">hello@agentflow.ventures</a>.
          General support:{" "}
          <a href="mailto:support@agentflow.ventures">support@agentflow.ventures</a>.
        </p>
        <p>
          AgentFlow Ventures Ltd.<br />
          Cayman Islands
        </p>
      </section>
    </LegalLayout>
  );
}
